import { isArray, isEqual, isNil, isObject, toPairs } from 'lodash-es';

import type {
  AiGenerated,
  MetadataFieldValue,
  MetadataFieldValuesPatched,
  MetadataValues,
  ModeType,
} from '@iconik-io/iconik-api/src/schema_patched/metadata';
import {
  AI_GENERATED,
  AI_GENERATED_ACCEPTED,
  AI_GENERATED_EDITED,
} from '@iconik-io/iconik-api/src/schema_patched/metadata';
import { isModeType } from '@iconik-io/shared-lib/src/utils/format_metadata_values';
import { elasticToForm } from '@iconik-io/shared-lib/src/utils/metadata_elastic_to_form';

import {
  FIELD_UPDATE_BULK_PREFIX,
  FIELD_UPDATE_MODE_PREFIX,
} from '../constants/data';

/**
 * Returns the keys of fields that actually changed relative to the original Redux values.
 * Filters out internal prefix keys and URL sub-field sentinels (e.g. `fieldName_0`,
 * `fieldName_0.label`) which are not real metadata fields.
 */
export const getChangedFieldKeys = (
  formValues: MetadataValues,
  originalValues: MetadataValues,
): string[] => {
  const normalized = elasticToForm(formValues);
  const allKeys = Object.keys(formValues).filter(
    (key) => !key.startsWith('__'),
  );
  // URL-type fields register synthetic sub-keys like `fieldName_0` and
  // `fieldName_0.label`/`fieldName_0.value`. Skip them — the parent key covers
  // the actual value.
  const isUrlSubField = (key: string): boolean => {
    const match = key.match(/^(.+)_\d+(\..*)?$/);
    return match ? allKeys.includes(match[1]) : false;
  };
  return allKeys.filter(
    (key) =>
      !isUrlSubField(key) && !isEqual(normalized[key], originalValues[key]),
  );
};

export const pickBulkMarkedFields = (data: MetadataValues): MetadataValues => {
  const result: MetadataValues = {};
  Object.entries(data).forEach(([key, value]) => {
    if (key.startsWith(FIELD_UPDATE_MODE_PREFIX)) {
      result[key] = value;
    } else if (key.startsWith(FIELD_UPDATE_BULK_PREFIX) && value) {
      const originalKey = key.replace(FIELD_UPDATE_BULK_PREFIX, '');
      result[originalKey] = data[originalKey];
    }
  });
  return result;
};

type MetadataFieldValueUpdate = {
  field_values?: {
    [k: string]: string | number | boolean;
  }[];
  mode?: ModeType;
};

type MetadataBulkUpdate = {
  [k: string]: MetadataFieldValueUpdate;
};

/**
 * Takes the server format used for bulk updates, converts it to the format used in the
 * form by flattening the field_values array and adds the bulk prefixes to mark those
 * fields that have been set with the checkbox that they will update
 *
 * @param data - The server format for bulk updates
 * @returns An object that can be passed in to the metadata form in bulk update mode
 */
export const serverBulkUpdateToForm = (
  data: MetadataBulkUpdate,
): MetadataValues => {
  const result: MetadataValues = {};
  Object.entries(data).forEach(([key, entry]) => {
    const fieldValues = entry.field_values;
    if (fieldValues) {
      result[key] = fieldValues.map((v) => ({ value: String(v.value) }));
      result[FIELD_UPDATE_BULK_PREFIX + key] = 'checked';
    }
    if (entry.mode) {
      result[FIELD_UPDATE_MODE_PREFIX + key] = entry.mode;
    }
  });

  return result;
};

const isEmptyValue = (fieldValue: MetadataFieldValue) =>
  String(fieldValue.value ?? '').trim() === '';

export const aggregateAiGenerated = (
  fieldValues: MetadataFieldValue[] | undefined,
): AiGenerated | undefined => {
  const filledValues = fieldValues?.filter(
    (fieldValue) => !isEmptyValue(fieldValue),
  );
  if (!filledValues?.length) {
    return undefined;
  }
  const flags = filledValues.map((fieldValue) => fieldValue.ai_generated);
  if (flags.every((flag) => isNil(flag))) {
    return undefined;
  }
  if (flags.some((flag) => flag === AI_GENERATED_EDITED || isNil(flag))) {
    return AI_GENERATED_EDITED;
  }
  return flags.some((flag) => flag === AI_GENERATED_ACCEPTED)
    ? AI_GENERATED_ACCEPTED
    : AI_GENERATED;
};

const toValueKeys = (fieldValues: MetadataFieldValue[]) =>
  fieldValues.map((fieldValue) => String(fieldValue.value ?? ''));

/**
 * Brings the AI flags the form drops back onto the values sent to the server.
 * An unchanged field keeps its previous flags. When a user changes a field
 * that held AI values, the AI values that are kept, or the value that
 * replaced a single AI value, become AI_EDITED. Values added by hand stay
 * unflagged and values that already carry a flag, such as the ones from the
 * AI review, are left as they are.
 */
export const markAiEdited = (
  previousValues: MetadataFieldValue[] | undefined,
  nextValues: MetadataFieldValue[],
  isSingleValue: boolean,
): MetadataFieldValue[] => {
  if (!previousValues || !aggregateAiGenerated(previousValues)) {
    return nextValues;
  }
  const previousFlags = new Map(
    previousValues
      .filter((fieldValue) => !isNil(fieldValue.ai_generated))
      .map((fieldValue) => [
        String(fieldValue.value ?? ''),
        fieldValue.ai_generated,
      ]),
  );
  const isUnchanged = isEqual(
    toValueKeys(previousValues),
    toValueKeys(nextValues),
  );
  return nextValues.map((fieldValue) => {
    if (!isNil(fieldValue.ai_generated) || isEmptyValue(fieldValue)) {
      return fieldValue;
    }
    const valueKey = String(fieldValue.value ?? '');
    if (isUnchanged) {
      const previousFlag = previousFlags.get(valueKey);
      return previousFlag
        ? { ...fieldValue, ai_generated: previousFlag }
        : fieldValue;
    }
    if (isSingleValue || previousFlags.has(valueKey)) {
      return { ...fieldValue, ai_generated: AI_GENERATED_EDITED };
    }
    return fieldValue;
  });
};

export const formToServerFormat = (
  currentValues: MetadataValues = {},
  data: MetadataValues = {},
): MetadataFieldValuesPatched => {
  const newData: MetadataFieldValuesPatched = {};
  Object.entries(data).forEach(([key, value]) => {
    let fieldValue: MetadataFieldValue[];
    if (isNil(value)) {
      fieldValue = [];
    } else if (isArray(value)) {
      fieldValue = value.map((item) =>
        isObject(item) ? item : { value: item },
      );
    } else {
      fieldValue = [{ value: String(value) }];
    }
    if (
      !isEqual(fieldValue, currentValues[key]) &&
      !key.startsWith(FIELD_UPDATE_MODE_PREFIX)
    ) {
      const previousValues = currentValues[key];
      newData[key] = {
        field_values: isArray(previousValues)
          ? markAiEdited(
              previousValues.filter(
                (previousValue): previousValue is MetadataFieldValue =>
                  isObject(previousValue),
              ),
              fieldValue,
              !isNil(value) && !isArray(value),
            )
          : fieldValue,
      };
      const modeValue = data[FIELD_UPDATE_MODE_PREFIX + key];
      if (isModeType(modeValue)) {
        newData[key].mode = modeValue;
      }
    }
  });
  return newData;
};

type AppStateMetadataFieldValues = Record<
  string,
  MetadataFieldValue[] | undefined
>;

export const serverToReduxFormat = (
  metadataValues: MetadataFieldValuesPatched,
): AppStateMetadataFieldValues => {
  const valuesDict: AppStateMetadataFieldValues = {};
  toPairs(metadataValues).forEach(([key, value]) => {
    valuesDict[key] = value.field_values?.map((fieldValue) => ({
      ...fieldValue,
      value: String(fieldValue.value ?? ''),
    }));
  });
  return valuesDict;
};
